<script lang="ts" setup>
import { computed, ref } from "vue";
import { useHotRuns, HOT_RUNS_DEFAULT_TAKE, type HotRun } from "@/queries/hot-runs";

/**
 * What the engine is spending on right now, run by run, most expensive first.
 *
 * The document-shaped half of this page. The charts say how much the platform spent in a window and are
 * then discarded; this names the individual runs, exact and unsampled — which is the step a metric spike
 * sends someone to once it has named an org.
 */
const props = defineProps<{
  /** The org the page is narrowed to, or undefined for the estate. */
  orgId?: string;
  /** Resolves an org id to its name, so a row reads as a tenant rather than a guid. */
  orgName: (orgId: string) => string;
}>();

const take = ref(HOT_RUNS_DEFAULT_TAKE);
const serverOrgId = computed(() => props.orgId);

const { hotRuns, openServices, scanCapped, hotRunsLoading, refetchHotRuns, readAt } = useHotRuns(
  serverOrgId,
  take
);

/** The run this row is about, where the run is still readable — a completed service loses the link. */
const runLink = (run: HotRun): string | null =>
  run.projectId && run.workflowId
    ? `/projects/${run.projectId}/history/${run.workflowId}/${run.workflowRunId}`
    : null;

const seconds = (ms: number) =>
  ms >= 60_000 ? `${(ms / 60_000).toFixed(1)} min` : `${(ms / 1000).toFixed(1)} s`;

const charge = (ru: number) => (ru >= 100 ? ru.toFixed(0) : ru.toFixed(1));

/**
 * How much of the run's time went to nobody — waiting for a permit. Beside its working time it is the
 * difference between a run that is slow and a run that is being held back.
 */
const throttled = (run: HotRun) =>
  run.live.throttledVendorMs + run.live.throttledInternalMs +
  run.live.throttledOrgMs + run.live.throttledPlatformMs;

const readAtLabel = computed(() =>
  readAt.value ? new Date(readAt.value).toLocaleTimeString() : "—"
);
</script>

<template>
  <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
    <h5 class="mb-0">Running now</h5>

    <span class="badge bg-tertiary">{{ openServices }} services in flight</span>

    <div class="ms-auto d-flex align-items-center gap-2">
      <span class="text-muted small">read {{ readAtLabel }}</span>
      <select v-model.number="take" class="form-select form-select-sm w-auto">
        <option :value="20">Top 20</option>
        <option :value="50">Top 50</option>
        <option :value="200">Top 200</option>
      </select>
      <button class="bb-btn-secondary-cta btn-sm" :disabled="hotRunsLoading" @click="refetchHotRuns()">
        <i class="bi bi-arrow-clockwise"></i>
      </button>
    </div>
  </div>

  <p class="text-muted small mb-3">
    Ranked by the work going through each run's open services, not by request units — a run on table
    storage is charged none and would rank last however hard it is working. Each number is current as of
    that service's last write.
  </p>

  <!-- The scan reads open services and ranks what it read. Past its ceiling the ranking is over a subset,
       which looks exactly like a ranking over everything unless it says so. -->
  <div v-if="scanCapped" class="alert alert-warning py-2 small">
    More than {{ openServices }} services are in flight, so this is the costliest of what was read rather
    than of what is running. Narrow to one org to see that org's whole picture.
  </div>

  <div v-if="hotRunsLoading && hotRuns.length === 0" class="text-center py-4">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading…</span>
    </div>
  </div>

  <div v-else-if="hotRuns.length === 0" class="text-center text-muted py-4">
    <i class="bi bi-pause-circle fs-3 d-block mb-2"></i>
    Nothing is running.
  </div>

  <div v-else class="table-responsive">
    <table class="table align-middle">
      <thead>
        <tr>
          <th>Org</th>
          <th>Run</th>
          <th class="text-end">Services</th>
          <th>Hosts</th>
          <th class="text-end">Working</th>
          <th class="text-end">Held</th>
          <th class="text-end">Store</th>
          <th class="text-end">Vendor calls</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="run in hotRuns" :key="`${run.orgId}-${run.workflowRunId}`">
          <td><span class="badge bg-dark">{{ orgName(run.orgId) }}</span></td>
          <td class="small">
            <router-link v-if="runLink(run)" :to="runLink(run)!">
              {{ run.workflowName }}
            </router-link>
            <span v-else class="text-muted font-monospace" :title="run.workflowRunId">
              {{ run.workflowRunId }}
            </span>
          </td>
          <td class="text-end">{{ run.openServices }}</td>
          <td class="small">
            <span v-if="run.hosts.length === 0" class="text-muted">between claims</span>
            <span v-for="host in run.hosts" :key="host" class="badge bg-secondary me-1">{{ host }}</span>
          </td>
          <td class="text-end text-nowrap">
            {{ seconds(run.live.computeMs) }}
            <!-- What the run's completed services already cost, off its own document. Context beside the
                 live number and deliberately not added to it: one says what this run is costing, the
                 other what it cost. -->
            <span v-if="run.final.computeMs > 0" class="text-muted small d-block">
              +{{ seconds(run.final.computeMs) }} done
            </span>
          </td>
          <td class="text-end text-nowrap">
            <span :class="throttled(run) > 0 ? 'text-warning' : 'text-muted'">
              {{ throttled(run) > 0 ? seconds(throttled(run)) : "—" }}
            </span>
          </td>
          <td class="text-end text-nowrap">
            {{ charge(run.live.requestCharge) }} RU
            <span class="text-muted small d-block">{{ run.live.storeRequests }} req</span>
          </td>
          <td class="text-end">{{ run.live.vendorCalls }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
